import { Component, DestroyRef, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { EMessageType } from 'src/app/shared/data/response-default.interface';
import { ToastService } from 'src/app/shared/services/toast.service';
import { IMedicalRecord, IPatient } from '../../models/patient.interface';
import { PatientService } from '../../services/patient.service';

// valores gravados pela triagem do totem (electron-app, modules/triage/constants/questions.ts)
const RESPOSTAS: Record<string, string> = { yes: 'SIM', no: 'NÃO', unknown: 'NÃO SEI' };
const HIPERTENSAO: Record<string, string> = {
  '2': 'SIM, e toma remédio',
  '1': 'SIM, mas não toma remédio',
  '0': 'NÃO',
  yes: 'SIM',
  no: 'NÃO'
};

@Component({
  selector: 'app-prontuario',
  templateUrl: './prontuario.component.html',
  styleUrls: ['./prontuario.component.scss']
})
export class ProntuarioComponent implements OnInit {
  medicalRecord?: IMedicalRecord;
  paciente?: IPatient;

  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly patientService: PatientService,
    private readonly toastService: ToastService,
    private readonly destroyRef: DestroyRef
  ) {}

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    const pacienteSelecionado = this.patientService.getSelectedPatient();

    if (pacienteSelecionado && pacienteSelecionado.id === id) {
      this.exibir(pacienteSelecionado);
      return;
    }

    // F5 ou link direto: o paciente em memória se perdeu, busca pela sessão.
    if (!id) {
      this.voltar();
      return;
    }

    // sem o takeUntilDestroyed, uma resposta que chega depois de sair da tela ainda redirecionaria para a lista
    this.patientService
      .buscarPaciente(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (paciente) => (paciente ? this.exibir(paciente) : this.naoEncontrado()),
        error: () => this.naoEncontrado()
      });
  }

  voltar(): void {
    this.router.navigate(['/dash/pacientes']);
  }

  /** "120/80"; só a sistólica quando a diastólica não veio; null sem pressão. */
  pressaoArterial(): string | null {
    const { SystolicPressure: sistolica, DiastolicPressure: diastolica } = this.medicalRecord ?? {};

    if (!sistolica) {
      return null;
    }

    return diastolica ? `${sistolica}/${diastolica}` : `${sistolica}`;
  }

  resposta(valor: string | null | undefined): string {
    return RESPOSTAS[(valor ?? '').toLowerCase()] ?? '-';
  }

  hipertensao(valor: string | null | undefined): string {
    return HIPERTENSAO[(valor ?? '').toLowerCase()] ?? '-';
  }

  formatDiagnosis(hipotese: string): string {
    if (!hipotese || hipotese.trim() === '') {
      return '';
    }

    const trimmed = hipotese.trim();

    // Extrai o número no início (pode ser decimal como 54.31)
    const match = trimmed.match(/^(\d+\.?\d*)\s+(.+)$/);

    if (!match) {
      return trimmed;
    }

    const numero = parseFloat(match[1]);
    let texto = match[2];

    // Remove tudo entre parênteses (incluindo os parênteses)
    texto = texto.replace(/\s*\([^)]*\)\s*/g, '').trim();

    // Converte o número para inteiro e adiciona %
    const numeroInteiro = Math.floor(numero);

    return `${numeroInteiro}% ${texto}`;
  }

  private exibir(paciente: IPatient): void {
    this.paciente = paciente;
    this.medicalRecord = paciente.medicalRecord;
  }

  private naoEncontrado(): void {
    this.toastService.toast('Prontuário não encontrado.', EMessageType.Error);
    this.voltar();
  }
}
